import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MatPaginator} from '@angular/material/paginator';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatSort, MatSortHeader} from '@angular/material/sort';
import {
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatHeaderRow,
    MatHeaderRowDef,
    MatRow,
    MatRowDef,
    MatTable,
    MatTableDataSource,
} from '@angular/material/table';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {catchError, tap} from 'rxjs/operators';
import {Item} from '../../models/item';
import {Observable, Subscription, throwError} from 'rxjs';
import {BreadCrumb} from '../../models/breadcrumb';
import {ExportItem} from '../../models/export-item';
import {
    collection,
    collectionData,
    deleteDoc,
    doc,
    docData,
    DocumentData,
    DocumentReference,
    Firestore,
    orderBy,
    query,
    Timestamp,
    where,
} from '@angular/fire/firestore';
import {stringify} from 'csv-stringify/browser/esm/sync';
import {BreadcrumbsComponent} from '../breadcrumbs/breadcrumbs.component';
import {ProgressComponent} from '../progress/progress.component';
import {MatCard, MatCardContent, MatCardHeader, MatCardSubtitle, MatCardTitle} from '@angular/material/card';
import {MatButton, MatMiniFabButton} from '@angular/material/button';
import {I18nPluralPipe} from '@angular/common';
import {MatInput} from '@angular/material/input';
import {MatFormField} from '@angular/material/form-field';

@Component({
    selector: 'app-inventory',
    templateUrl: './items.component.html',
    styleUrls: ['./items.component.css'],
    imports: [
        BreadcrumbsComponent,
        ProgressComponent,
        MatCard,
        MatCardHeader,
        MatCardTitle,
        MatCardSubtitle,
        MatFormField,
        MatButton,
        I18nPluralPipe,
        MatInput,
        MatTable,
        MatSort,
        MatSortHeader,
        MatColumnDef,
        MatHeaderCellDef,
        MatHeaderCell,
        MatCell,
        RouterLink,
        MatCellDef,
        MatHeaderRow,
        MatHeaderRowDef,
        MatRow,
        MatRowDef,
        MatPaginator,
        MatCardContent,
        MatMiniFabButton
    ],
    standalone: true
})

export class ItemsComponent implements OnInit, OnDestroy {

    @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
    @ViewChild(MatSort, {static: true}) sort: MatSort;
    facilityId = '';
    locationId = '';
    facilityName = '';
    locationName = '';
    dataSource: MatTableDataSource<DocumentData>;
    facility: Observable<DocumentData>;
    location: Observable<DocumentData>;
    displayedColumns: string[] = ['customerPartNumber', 'location', 'uniqueItemId', 'itemType', 'eclipseId', 'actions'];
    breadcrumbs: BreadCrumb[];
    itemPluralMapping = {
        '=0': '0 Items',
        '=1': '1 Item',
        other: '# Items'
    };
    private subscription: Subscription;
    private facilitySubscription: Subscription;
    private locationSubscription: Subscription;
    private facilityDocument: DocumentReference;
    private locationDocument: DocumentReference;

    constructor(private firestore: Firestore, private route: ActivatedRoute, private snackBar: MatSnackBar) {
    }

    ngOnInit() {
        this.breadcrumbs = [
            {url: ['/'], title: 'Accounts'},
            {url: null, title: 'Facilities'},
            {url: ['../../'], title: 'Facility'},
            {url: null, title: 'Location'},
        ];

        this.route.paramMap.subscribe(params => {
            this.facilityId = params.get('facilityId')!;
            this.locationId = params.get('locationId')!;
            const facilityDocRef = doc(this.firestore, 'facilities', this.facilityId);
            this.facilityDocument = facilityDocRef;
            this.facility = docData(facilityDocRef);
            this.facilitySubscription = this.facility.pipe(
                tap(data => {
                    this.facilityName = data.name;
                    this.breadcrumbs[1].url = ['/facilities', data.accountId];
                    this.breadcrumbs[2].title = `Facility: ${data.name}`;
                })
            ).subscribe();
            const locationDocRef = doc(
                this.firestore,
                'facilities',
                this.facilityId,
                'locations',
                this.locationId
            );
            this.locationDocument = locationDocRef;
            this.location = docData(locationDocRef);
            this.locationSubscription = this.location.pipe(
                tap(data => {
                    this.locationName = data.name;
                    this.breadcrumbs[3].title = `Location: ${data.name}`;
                })
            ).subscribe();
            const itemsColRef = collection(this.firestore, 'items');
            const itemsQuery = query(
                itemsColRef,
                where('facility', '==', `facilities/${this.facilityId}`),
                where('location', '==',  `locations/${this.locationId}`),
                orderBy('customerPartNumber', 'asc')
            );
            this.subscription = collectionData(itemsQuery, {idField: 'id'}).pipe(
                catchError(e => throwError(() => e))
            ).subscribe({
                    next: data => {
                        this.dataSource = new MatTableDataSource(data);
                        this.dataSource.sort = this.sort;
                        this.dataSource.paginator = this.paginator;
                    },
                    error: error => {
                        console.log(error);
                    },
                }
            );
        });

    }

    deleteItem(a: Item) {
        if (confirm(`Are you sure you want to delete ${a.uniqueItemId}`)) {
            const itemDocRef = doc(this.firestore, 'items', a.id);
            deleteDoc(itemDocRef)
                .then(() => {
                    this.snackBar.open('Item Deleted', null, {duration: 3000});
                })
                .catch(error => {
                    this.snackBar.open('Error Deleting Item: ' + error, null, {duration: 3000});
                });
        }
    }


    ngOnDestroy(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
        if (this.locationSubscription) {
            this.locationSubscription.unsubscribe();
        }
        if (this.facilitySubscription) {
            this.facilitySubscription.unsubscribe();
        }
    }

    search(event: any) {
        this.dataSource.filter = event.target.value.trim().toLowerCase();
    }

    downloadCsv() {
        const exports: ExportItem[] = [];
        for (const item of this.dataSource.data) {
            const timestamp = item.fifoDate as Timestamp;
            const tmp: ExportItem = {} as ExportItem;
            tmp.tag = item.tag;
            tmp.customerPartNumber = item.customerPartNumber;
            tmp.destination = item.destination;
            tmp.eclipseId = item.eclipseId;
            tmp.facility = item.facilityName;
            tmp.fifoDate = timestamp.toDate().toISOString();
            tmp.foundDuringLastCycleCount = item.foundDuringLastCycleCount ? 'Yes' : 'No';
            tmp.itemDescription = item.itemDescription;
            tmp.itemType = item.itemType;
            tmp.location = item.locationName;
            tmp.pull = item.pull;
            tmp.quantity = item.quantity.toString();
            tmp.source = item.source;
            tmp.palletized = item.palletized ? 'Yes' : 'No';
            tmp.palletNumber = item.palletNumber;
            switch (item.status) {
                case 0:
                    tmp.status = 'Available To Ship';
                    break;
                case 1:
                    tmp.status = 'Shipped';
                    break;
                case 2:
                    tmp.status = 'Received';
                    break;
                case 3:
                    tmp.status = 'Available for Requisition';
                    break;
                case 4:
                    tmp.status = 'Requisitioned to Jobsite';
                    break;
            }
            switch (item.flag) {
                case 0:
                    tmp.flag = 'Normal';
                    break;
                case 1:
                    tmp.flag = 'Bypassed Receive workflow';
                    break;
                case 2:
                    tmp.flag = 'Bypassed Putaway workflow';
                    break;
                default:
                    tmp.flag = 'Normal';
                    break;
            }
            exports.push(tmp);
        }
        const csv = stringify(exports, {
            header: true,
            columns: [
                {key: 'tag', header: 'tag'},
                {key: 'customerPartNumber', header: 'customerPartNumber'},
                {key: 'destination', header: 'destination'},
                {key: 'eclipseId', header: 'eclipseId'},
                {key: 'facility', header: 'facility'},
                {key: 'fifoDate', header: 'fifoDate'},
                {key: 'foundDuringLastCycleCount', header: 'foundDuringLastCycleCount'},
                {key: 'itemDescription', header: 'itemDescription'},
                {key: 'itemType', header: 'itemType'},
                {key: 'location', header: 'location'},
                {key: 'pull', header: 'pull'},
                {key: 'quantity', header: 'quantity'},
                {key: 'source', header: 'source'},
                {key: 'palletized', header: 'palletized'},
                {key: 'palletNumber', header: 'palletNumber'},
                {key: 'status', header: 'status'},
                {key: 'flag', header: 'flag'},
            ],
        });
        this.download(`${this.facilityName}-${this.locationName}-export.csv`,
            csv);
    }

    download(filename, text) {
        const element = document.createElement('a');
        element.setAttribute('href', 'data:text/csv;charset=utf-8,' + encodeURIComponent(text));
        element.setAttribute('download', filename);
        element.style.display = 'none';
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
    }
}
